import type { Approval, MembershipProject } from "@zworks/console-contracts";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  CirclePlay,
  Ellipsis,
  ShieldCheck,
  PanelLeft,
  Search,
  Sparkles,
} from "lucide-react";
import * as React from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useMatch,
  useMatches,
  useNavigate,
  useSearchParams,
} from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { approvalsInboxQuery, currentUserQuery, metaQuery, myProjectsQuery } from "@/lib/api";
import { formatCredits } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CommandDialogs } from "./shell/command-dialogs";
import { useCommandSurface } from "./shell/use-command-surface";
import { marketplaceItem, navGroups, primaryActions, type NavItem } from "./shell/nav";
import { ProfileMenu } from "./shell/profile-menu";
import { SettingsDialog } from "./shell/settings-dialog";
import { ProjectSwitcher } from "./shell/project-switcher";
import { NotificationsMenu } from "./shell/notifications-menu";
import { QuickThemeToggle } from "./shell/quick-theme-toggle";
import { HelpMenu } from "./shell/help-menu";
import { FeedbackDialog } from "./shell/feedback-dialog";
import { SlowRequestNoticeWhenIdle } from "./shell/slow-request-notice";

const environmentLabels: Record<string, string> = {
  development: "Development",
  staging: "Staging",
  production: "Production",
};

/** The drawer's tab stops in document order, so Tab can wrap from the last back to the first. */
function drawerFocusables(container: HTMLElement): HTMLElement[] {
  return [
    ...container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ].filter((element) => element.checkVisibility?.() ?? true);
}

/** What a page behind the shell knows: the project the switcher has selected, if there is one. */
export interface ShellOutletContext {
  currentProject: MembershipProject | null;
}

/**
 * The frame every project page lives in. Collapsing changes only the width: the selected row, its
 * route, and `aria-current` stay exactly where they were.
 */
export function AppShell() {
  const [sidebarPreference, setSidebarPreference] = React.useState<{
    userId: string;
    collapsed: boolean;
  } | null>(null);
  const [surface, setSurface] = useCommandSurface();
  const [currentProjectId, setCurrentProjectId] = React.useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [mobileNavigationOpen, setMobileNavigationOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const location = useLocation();

  const me = useQuery(currentUserQuery);
  const userId = me.data?.id;
  const collapsed = userId
    ? sidebarPreference?.userId === userId
      ? sidebarPreference.collapsed
      : localStorage.getItem(`zworks:sidebar-collapsed:${userId}`) === "true"
    : false;
  const memberships = useQuery(myProjectsQuery);
  const projects = memberships.data ?? [];
  const current =
    projects.find((project) => project.id === currentProjectId) ??
    projects.find((project) => project.id === searchParams.get("project")) ??
    projects[0] ??
    null;
  const approvals = useQuery({
    ...approvalsInboxQuery(current?.id ?? ""),
    enabled: current !== null,
  });
  const waitingApprovals = approvals.data?.waitingForMe ?? [];
  const approvalCount = waitingApprovals.length;
  const pageContext = usePageTitle();

  const handleOpenZot = () => {
    if (location.pathname === "/zot") {
      const composer =
        document.getElementById("zot-message") ?? document.getElementById("zot-home-message");
      if (composer) {
        composer.focus();
        return;
      }
    }
    setSurface("zot");
  };

  const toggleSidebar = () => {
    if (!userId) return;
    const next = !collapsed;
    localStorage.setItem(`zworks:sidebar-collapsed:${userId}`, String(next));
    setSidebarPreference({ userId, collapsed: next });
  };

  const navigate = useNavigate();
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey &&
        event.key === ","
      ) {
        event.preventDefault();
        setSettingsOpen(true);
        return;
      }
      // New task opens the Zot home as an empty chat. typingContext skips fields so text
      // entry, the command palette, and dialogs keep their keys.
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() !== "n" || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      const typingContext = target?.closest(
        "input, textarea, select, [contenteditable], [role='dialog'], [role='listbox'], [role='menu']",
      );
      if (typingContext) return;
      event.preventDefault();
      navigate("/zot");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);

  React.useEffect(() => {
    if (surface === "zot" && location.pathname === "/zot") {
      setSurface(null);
      document.getElementById("zot-message")?.focus();
      document.getElementById("zot-home-message")?.focus();
    }
  }, [surface, location.pathname, setSurface]);

  React.useEffect(() => {
    if (!mobileNavigationOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileNavigationOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileNavigationOpen]);

  return (
    <div className="flex min-h-svh">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Sidebar
        collapsed={collapsed}
        onToggle={toggleSidebar}
        projects={projects}
        projectsLoading={memberships.isPending}
        current={current}
        approvalCount={approvalCount}
        onSelectProject={(project) => {
          setCurrentProjectId(project.id);
          setMobileNavigationOpen(false);
        }}
        initials={me.data?.initials ?? ""}
        displayName={me.data?.displayName ?? ""}
        mobileOpen={mobileNavigationOpen}
        onCloseMobile={() => setMobileNavigationOpen(false)}
        onOpenSettings={() => setSettingsOpen(true)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          projectName={current?.name ?? null}
          projects={projects}
          current={current}
          projectsLoading={memberships.isPending}
          waitingApprovals={waitingApprovals}
          onSelectProject={(project) => setCurrentProjectId(project.id)}
          onOpenMobileNavigation={() => setMobileNavigationOpen(true)}
          onOpenSearch={() => setSurface("search")}
          onOpenZot={handleOpenZot}
          onOpenFeedback={() => setFeedbackOpen(true)}
        />
        <SlowRequestNoticeWhenIdle />
        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            "min-w-0 flex-1 pb-20 md:pb-0",
            pageContext === "Flow editor" &&
              "flex flex-col h-[calc(100vh-3.5rem)] md:h-[calc(100vh-3rem)] pb-0 overflow-hidden",
          )}
        >
          <div
            className={cn(
              "w-full flex flex-col",
              pageContext === "Flow editor"
                ? "h-full flex-1 p-2 md:p-3 overflow-hidden"
                : "mx-auto max-w-6xl gap-4 p-4 md:gap-6 md:p-6",
            )}
          >
            <Outlet context={{ currentProject: current } satisfies ShellOutletContext} />
            {location.pathname === "/zot" ? null : (
              <Button
                variant="default"
                size="icon"
                className={cn(
                  "z-30 size-12 rounded-full shadow-md",
                  pageContext === "Flow editor"
                    ? "fixed right-4 bottom-4 md:right-6 md:bottom-6"
                    : "ml-auto md:fixed md:right-4 md:bottom-6",
                )}
                aria-label="Ask Zot"
                onClick={handleOpenZot}
              >
                <Sparkles aria-hidden />
              </Button>
            )}
          </div>
        </main>
      </div>
      <MobileNavigation approvalCount={approvalCount} />
      <CommandDialogs
        surface={surface}
        onClose={() => setSurface(null)}
        project={current}
        pageContext={pageContext}
        onOpenZot={handleOpenZot}
      />
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        projectName={current?.name ?? null}
        pageContext={pageContext}
      />
      <SettingsDialog project={current} open={settingsOpen} onOpenChange={setSettingsOpen} />
      <Toaster />
    </div>
  );
}

function Sidebar({
  collapsed,
  onToggle,
  projects,
  projectsLoading,
  current,
  approvalCount,
  onSelectProject,
  initials,
  displayName,
  mobileOpen,
  onCloseMobile,
  onOpenSettings,
}: {
  collapsed: boolean;
  onToggle: () => void;
  projects: MembershipProject[];
  projectsLoading: boolean;
  current: MembershipProject | null;
  approvalCount: number;
  onSelectProject: (project: MembershipProject) => void;
  initials: string;
  displayName: string;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onOpenSettings: () => void;
}) {
  const navigate = useNavigate();
  const visuallyCollapsed = collapsed && !mobileOpen;
  const asideRef = React.useRef<HTMLElement>(null);
  const returnFocusRef = React.useRef<Element | null>(null);

  // The drawer is the modal surface at mobile widths, so it behaves like one: focus moves in on
  // open, stays inside while it is open, and returns to the control that opened it on close.
  React.useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;
    if (!mobileOpen) {
      const returnTo = returnFocusRef.current;
      returnFocusRef.current = null;
      if (returnTo instanceof HTMLElement && returnTo.isConnected) returnTo.focus();
      return;
    }
    returnFocusRef.current = document.activeElement;
    drawerFocusables(aside)[0]?.focus();
  }, [mobileOpen]);

  const keepFocusInside = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!mobileOpen || event.key !== "Tab") return;
    const focusable = drawerFocusables(event.currentTarget);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-40 bg-foreground/30 md:hidden"
          onClick={onCloseMobile}
        />
      ) : null}
      <aside
        ref={asideRef}
        onKeyDown={keepFocusInside}
        aria-label="Sidebar"
        className={cn(
          "invisible fixed inset-y-0 left-0 z-50 flex w-72 shrink-0 -translate-x-full flex-col border-r bg-sidebar transition-transform duration-150 md:visible md:static md:z-20 md:translate-x-0 md:transition-[width]",
          mobileOpen && "visible translate-x-0",
          collapsed ? "md:w-[3.75rem]" : "md:w-64",
        )}
      >
        {/* Brand header */}
        <div
          className={cn(
            "flex h-14 shrink-0 items-center border-b border-border/60 px-4 transition-all",
            visuallyCollapsed ? "justify-center px-1" : "gap-3",
          )}
        >
          <span className={cn("brand-z", visuallyCollapsed && "text-2xl mr-0 pl-1")}>Z</span>
          {!visuallyCollapsed && (
            <div className="flex flex-col leading-none">
              <span className="text-[15px] font-bold tracking-tight text-foreground">
                zworks<span className="text-muted-foreground">.</span>
              </span>
              <span className="mt-1 font-mono text-xs font-bold tracking-[0.18em] text-muted-foreground uppercase">
                CONTROL / PLANE
              </span>
            </div>
          )}
        </div>

        <div className={cn("flex items-center gap-1 p-2", visuallyCollapsed && "flex-col")}>
          <ProjectSwitcher
            projects={projects}
            currentId={current?.id ?? null}
            collapsed={visuallyCollapsed}
            onSelect={(project) => {
              onSelectProject(project);
              onCloseMobile();
            }}
            onCreateProject={() => void navigate("/projects")}
            loading={projectsLoading}
            buttonClassName="h-11 md:h-[1.875rem]"
          />
        </div>

        {/* Rounded-full toggle button sticky vertically centered on the right edge */}
        <Button
          variant="outline"
          size="icon"
          className="hidden md:flex absolute top-1/2 -right-3 z-30 size-6 -translate-y-1/2 rounded-full border bg-background p-0 shadow-sm hover:bg-accent"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          onClick={onToggle}
        >
          {collapsed ? (
            <ChevronRight className="size-3.5" aria-hidden />
          ) : (
            <ChevronLeft className="size-3.5" aria-hidden />
          )}
        </Button>

        <nav
          aria-label="Primary"
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-2"
        >
          {primaryActions.map((item) => (
            <SidebarLink
              key={item.to}
              item={item}
              collapsed={visuallyCollapsed}
              badgeCount={item.badge === "approvals" ? approvalCount : undefined}
              onNavigate={onCloseMobile}
            />
          ))}
          {navGroups.map((group) => (
            <div key={group.label || "top"} className="flex flex-col gap-0.5">
              {visuallyCollapsed ? (
                <div aria-hidden className="mx-auto my-1.5 h-px w-6 bg-border" />
              ) : group.label ? (
                <p className="px-2 pt-2.5 pb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  {group.label}
                </p>
              ) : null}
              {group.items.map((item) => (
                <SidebarLink
                  key={item.to}
                  item={item}
                  collapsed={visuallyCollapsed}
                  badgeCount={item.badge === "approvals" ? approvalCount : undefined}
                  onNavigate={onCloseMobile}
                />
              ))}
            </div>
          ))}
        </nav>

        <div className="mt-auto flex shrink-0 flex-col gap-1 border-t p-2">
          <Credits current={current} collapsed={visuallyCollapsed} />
          <SidebarLink
            item={marketplaceItem}
            collapsed={visuallyCollapsed}
            onNavigate={onCloseMobile}
          />
          {displayName && (
            <ProfileMenu
              initials={initials}
              displayName={displayName}
              role={current?.role ?? null}
              projectName={current?.name ?? null}
              collapsed={visuallyCollapsed}
              onOpenSettings={onOpenSettings}
            />
          )}
        </div>
      </aside>
    </>
  );
}

function SidebarLink({
  item,
  collapsed,
  badgeCount,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  badgeCount?: number;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const isActive = useMatch({ path: item.to, end: item.end ?? item.to === "/" }) !== null;
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      aria-label={
        collapsed && badgeCount !== undefined && badgeCount > 0
          ? `${item.label}, ${badgeCount} waiting`
          : collapsed
            ? item.label
            : undefined
      }
      className={cn(
        "relative flex h-11 shrink-0 items-center gap-2.5 rounded-lg px-2 text-sm text-foreground/80 hover:bg-sidebar-accent hover:text-foreground transition-colors md:h-[1.875rem]",
        collapsed && "justify-center px-0 md:mx-auto md:h-9 md:w-9",
        isActive &&
          "bg-sidebar-accent font-semibold text-foreground before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4.5 before:w-[3px] before:rounded-r before:bg-chart shadow-2xs",
      )}
    >
      <span className="relative">
        <Icon
          className={cn("size-4 shrink-0 transition-colors", isActive && "text-accent-text")}
          aria-hidden
        />
        {collapsed && badgeCount !== undefined && badgeCount > 0 && (
          <span
            aria-hidden
            className="absolute -top-1 -right-1 min-w-4 rounded-full border border-sidebar bg-chart px-1 text-center font-mono text-xs leading-[0.875rem] font-bold text-primary-foreground shadow-xs"
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </span>
      <span className={cn(collapsed && "sr-only")}>{item.label}</span>
      {!collapsed && badgeCount !== undefined && badgeCount > 0 && (
        <span className="ml-auto rounded-full bg-chart/10 border border-chart/30 px-1.5 py-0.2 font-mono text-xs font-bold text-accent-text">
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}
    </NavLink>
  );
  if (!collapsed) return link;
  const tooltip =
    badgeCount !== undefined && badgeCount > 0
      ? `${item.label}, ${badgeCount} waiting`
      : item.label;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The balance is unknown until a wallet exists, so it is labelled as unknown rather than shown as
 * zero. Top up is the owner's action and is not wired until billing exists.
 */
function Credits({
  current,
  collapsed,
}: {
  current: MembershipProject | null;
  collapsed: boolean;
}) {
  if (collapsed || !current) return null;
  const balance = current.creditBalance;
  return (
    <div className="rounded-md px-2 py-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Credits</span>
        {current.role === "owner" && (
          <Link
            to="/checkout"
            className="inline-flex h-11 items-center px-1 text-xs font-medium text-accent-text hover:underline md:h-auto md:px-0"
          >
            Top up
          </Link>
        )}
      </div>
      <p className="mt-1 text-sm tabular-nums">
        {balance === null ? (
          <Badge variant="unknown">Unknown</Badge>
        ) : (
          <>
            <b>{formatCredits(balance)}</b> <span className="text-muted-foreground">left</span>
          </>
        )}
      </p>
    </div>
  );
}

function TopBar({
  projectName,
  projects,
  current,
  projectsLoading,
  waitingApprovals,
  onSelectProject,
  onOpenMobileNavigation,
  onOpenSearch,
  onOpenZot,
  onOpenFeedback,
}: {
  projectName: string | null;
  projects: MembershipProject[];
  current: MembershipProject | null;
  projectsLoading: boolean;
  waitingApprovals: Approval[];
  onSelectProject: (project: MembershipProject) => void;
  onOpenMobileNavigation: () => void;
  onOpenSearch: () => void;
  onOpenZot: () => void;
  onOpenFeedback: () => void;
}) {
  const navigate = useNavigate();
  const title = usePageTitle();

  return (
    <header className="relative flex h-14 shrink-0 items-center justify-between border-b px-2 sm:gap-3 sm:px-4 md:h-12 md:grid md:grid-cols-[1fr_auto_1fr]">
      {/* Left side: mobile navigation and project switcher */}
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex items-center gap-2 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            className="size-11 shrink-0"
            aria-label="Open navigation"
            onClick={onOpenMobileNavigation}
          >
            <PanelLeft />
          </Button>
          <ProjectSwitcher
            projects={projects}
            currentId={current?.id ?? null}
            collapsed={false}
            onSelect={onSelectProject}
            onCreateProject={() => void navigate("/projects")}
            loading={projectsLoading}
            buttonClassName="h-11 border bg-card px-2"
          />
        </div>
      </div>

      {/* Centred global search control */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          size="sm"
          className="h-11 gap-2 rounded-md px-2.5 text-xs text-muted-foreground hover:text-foreground md:h-8 md:w-64 lg:w-80 justify-between"
          onClick={onOpenSearch}
          aria-label="Search"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Search className="size-3.5 shrink-0" aria-hidden />
            <span className="hidden sm:inline truncate">Search runs, workflows, decisions…</span>
            <span className="sm:hidden">Search…</span>
          </div>
          <kbd className="pointer-events-none hidden h-4.5 select-none items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground sm:inline-flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      </div>

      {/* Right side: top navbar actions */}
      <div className="flex items-center justify-end gap-1 sm:gap-1.5">
        <div className="hidden lg:block">
          <EnvironmentBadge />
        </div>
        <NotificationsMenu projectId={current?.id ?? null} waitingApprovals={waitingApprovals} />
        <QuickThemeToggle />
        <HelpMenu
          projectName={projectName}
          pageContext={title}
          onOpenZot={onOpenZot}
          onOpenFeedback={onOpenFeedback}
        />
      </div>
    </header>
  );
}

function MobileNavigation({ approvalCount }: { approvalCount: number }) {
  const [moreOpen, setMoreOpen] = React.useState(false);
  const location = useLocation();
  const tabs = [
    { to: "/zot", label: "Zot", icon: Sparkles, end: false },
    { to: "/runs", label: "Runs", icon: CirclePlay, end: false },
    { to: "/approvals", label: "Approvals", icon: ShieldCheck, end: false },
  ];
  const moreItems = navGroups
    .flatMap((group) => group.items)
    .filter((item) => !tabs.some((tab) => tab.to === item.to));
  const moreActive = moreItems.some((item) =>
    item.end || item.to === "/"
      ? location.pathname === item.to
      : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
  );
  return (
    <>
      <nav
        aria-label="Mobile primary"
        className="mobile-bottom-nav fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t bg-background md:hidden"
      >
        {tabs.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                "flex min-h-14 flex-col items-center justify-center gap-0.5 px-1 text-xs text-muted-foreground focus-visible:z-10",
                isActive && "font-semibold text-foreground",
              )
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <Icon className={cn("size-4", isActive && "text-accent-text")} aria-hidden />
                  {label === "Approvals" && approvalCount > 0 ? (
                    <span className="absolute -top-2 -right-3 min-w-4 rounded-full bg-primary px-1 text-center text-xs leading-4 text-primary-foreground">
                      {approvalCount > 99 ? "99+" : approvalCount}
                    </span>
                  ) : null}
                </span>
                <span>{label}</span>
                {label === "Approvals" ? (
                  <span className="sr-only">, {approvalCount} waiting</span>
                ) : null}
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          aria-label="More destinations"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
          className={cn(
            "flex min-h-14 flex-col items-center justify-center gap-0.5 px-1 text-xs text-muted-foreground",
            moreActive && "font-semibold text-foreground",
          )}
        >
          <Ellipsis className={cn("size-4", moreActive && "text-accent-text")} aria-hidden />
          <span>More</span>
        </button>
      </nav>
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl p-0 md:hidden">
          <SheetTitle className="px-4 pt-4">More</SheetTitle>
          <SheetDescription className="px-4">
            Every destination beyond the primary bar.
          </SheetDescription>
          <nav aria-label="More destinations" className="max-h-[60svh] overflow-y-auto p-2">
            {navGroups.map((group) => {
              const visible = group.items.filter((item) => !tabs.some((tab) => tab.to === item.to));
              if (visible.length === 0) return null;
              return (
                <div key={group.label || "top"} className="flex flex-col">
                  {group.label ? (
                    <p className="px-2 pt-3 pb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                      {group.label}
                    </p>
                  ) : null}
                  {visible.map((item) => (
                    <MoreLink key={item.to} item={item} onNavigate={() => setMoreOpen(false)} />
                  ))}
                </div>
              );
            })}
            {moreItems.length === 0 ? (
              <p className="px-2 py-4 text-sm text-muted-foreground">No further destinations.</p>
            ) : null}
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
}

function MoreLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex h-11 items-center gap-2.5 rounded-lg px-2 text-sm text-foreground/80 hover:bg-accent hover:text-foreground",
          isActive && "bg-accent font-semibold text-foreground",
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span>{item.label}</span>
    </NavLink>
  );
}

function EnvironmentBadge() {
  const meta = useQuery(metaQuery);
  if (!meta.data) return null;
  const label = environmentLabels[meta.data.environment] ?? meta.data.environment;
  return (
    <div className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card px-2.5 py-1 text-xs text-muted-foreground shadow-2xs">
      <span className="dot-indicator" />
      <span className="font-medium text-foreground">{label}</span>
    </div>
  );
}

/** The current page's title, taken from the route handle so the breadcrumb matches the page. */
function usePageTitle(): string {
  const matches = useMatches();
  for (const match of [...matches].reverse()) {
    const handle = match.handle as { title?: string } | undefined;
    if (handle?.title) return handle.title;
  }
  return "";
}
